/* FAQ hub (OL-3530, epic OL-3521 Enhanced PDP v2).
   All FAQs are rendered once by Liquid with data-category attributes;
   this element only toggles visibility. No re-fetch, no re-render. */

class FaqHub extends HTMLElement {
  connectedCallback() {
    this.categoryRow = this.querySelector('[data-faq-category-row]');
    this.categoryButtons = Array.from(this.querySelectorAll('[data-faq-category]'));
    this.items = Array.from(this.querySelectorAll('[data-faq-item]'));
    this.chips = Array.from(this.querySelectorAll('[data-faq-chip]'));

    this.categoryButtons.forEach((button) => {
      button.addEventListener('click', () => this.setCategory(button.dataset.faqCategory));
    });

    this.chips.forEach((chip) => {
      chip.addEventListener('click', () => this.openQuestion(chip.dataset.faqTarget));
    });

    // First category is active by default (server-rendered); re-apply so the
    // DOM state is consistent after theme editor re-renders.
    const active =
      this.querySelector('[data-faq-category].is-active') || this.categoryButtons[0];
    if (active) this.setCategory(active.dataset.faqCategory);
  }

  get reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  setCategory(categoryHandle, { revealCategory = false } = {}) {
    let activeButton = null;

    this.categoryButtons.forEach((button) => {
      const isActive = button.dataset.faqCategory === categoryHandle;
      if (isActive) activeButton = button;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.items.forEach((item) => {
      item.hidden = item.dataset.category !== categoryHandle;
    });

    if (revealCategory && activeButton) this.revealCategory(activeButton);
  }

  /* OL-3562: the category row is a horizontal scroller on mobile
     (section-faq-hub.css) and a grid with no overflow on desktop. When the
     active category is switched for the user - rather than tapped by them -
     the newly active pill can sit off-screen, so the category appears not to
     change at all. Scrolls the row itself rather than calling
     scrollIntoView() on the pill, which would also drag the page vertically
     and fight the item scroll in openQuestion(). */
  revealCategory(button) {
    const row = this.categoryRow;
    if (!row) return;

    // Desktop grid has nothing to scroll - skip rather than animate a no-op.
    if (row.scrollWidth <= row.clientWidth) return;

    const rowRect = row.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    // Rect delta, not offsetLeft: correct regardless of which ancestor
    // happens to be the offset parent.
    const left = buttonRect.left - rowRect.left - (rowRect.width - buttonRect.width) / 2;

    row.scrollBy({ left, behavior: this.reduceMotion ? 'auto' : 'smooth' });
  }

  openQuestion(questionHandle) {
    const item = this.items.find((entry) => entry.dataset.question === questionHandle);
    if (!item) return;

    this.setCategory(item.dataset.category, { revealCategory: true });
    item.open = true;
    // global.js's click listener is what normally syncs this, so it only
    // fires for a real click on the summary - opening a FAQ from a chip
    // bypasses that click entirely, so it's set directly here (OL-3530
    // review fix).
    item.querySelector('summary')?.setAttribute('aria-expanded', 'true');

    item.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'center' });
  }
}

if (!customElements.get('faq-hub')) {
  customElements.define('faq-hub', FaqHub);
}
